import React, { useState } from 'react'
import Modal from './Modal'
import { Field, inputClass } from './Field'
import { BalanceTarget } from '../types'
import { makeId } from '../lib/id'
import { TargetAchievement } from '../lib/cashflowEngine'
import { formatMoney, formatDateShort } from '../lib/format'
import { fromISODate } from '../lib/dateRules'

interface Props {
  initial?: BalanceTarget
  achievements?: TargetAchievement[]
  currencySymbol?: string
  todayIso?: string
  onJumpToDate?: (iso: string) => void
  onSave: (item: BalanceTarget, addAnother: boolean) => void
  onDelete?: () => void
  onClose: () => void
}

export default function TargetForm({
  initial,
  achievements,
  currencySymbol = '£',
  todayIso = '',
  onJumpToDate,
  onSave,
  onDelete,
  onClose,
}: Props) {
  const [name, setName] = useState(initial?.name ?? '')
  const [amount, setAmount] = useState(initial?.amount?.toString() ?? '')
  const [consecutiveDays, setConsecutiveDays] = useState((initial?.consecutiveDays ?? 1).toString())
  const [ratchet, setRatchet] = useState(!!initial?.raiseBy)
  const [raiseBy, setRaiseBy] = useState(initial?.raiseBy?.toString() ?? '')
  const [style, setStyle] = useState<BalanceTarget['style']>(initial?.style ?? 'gold')
  const [active, setActive] = useState(initial?.active ?? true)

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    const submitter = (e.nativeEvent as SubmitEvent).submitter as HTMLButtonElement | null
    const addAnother = submitter?.dataset.action === 'add-another'
    const item: BalanceTarget = {
      id: initial?.id ?? makeId(),
      name: name.trim() || 'Target',
      amount: Math.abs(parseFloat(amount) || 0),
      consecutiveDays: Math.max(1, Math.floor(parseFloat(consecutiveDays) || 1)),
      raiseBy: ratchet && raiseBy ? Math.abs(parseFloat(raiseBy)) : undefined,
      style,
      active,
    }
    onSave(item, addAnother)
  }

  return (
    <Modal title={`${initial ? 'Edit' : 'Add'} target`} onClose={onClose}>
      <form onSubmit={handleSubmit}>
        <p className="text-xs text-ink/70 mb-4">
          RAG thresholds mark danger; a target marks a level worth reaching. Days that qualify get
          highlighted on the calendar.
        </p>

        {initial && achievements && achievements.length > 0 && (() => {
          const reached = achievements.filter((a) => a.date <= todayIso)
          // A ratcheting target clears a rung every couple of weeks, so the
          // full three-year list is mostly noise — keep what's been reached
          // and what's coming up next.
          const upcoming = achievements.filter((a) => a.date > todayIso)
          const shown = [...reached.slice(-4), ...upcoming.slice(0, 8)]
          const hidden = achievements.length - shown.length

          return (
            <div className="rounded-sm bg-gold-bg ring-1 ring-gold px-3 py-2 mb-4">
              <div className="text-[11px] font-sans uppercase tracking-wide text-gold-deep mb-1">
                {reached.length === 0
                  ? 'On track to reach'
                  : reached.length === 1
                  ? 'Achieved'
                  : `Achieved · ${reached.length} times`}
              </div>
              <ul className="space-y-0.5">
                {shown.map((a) => (
                  <li key={`${a.date}-${a.level}`} className="flex items-center justify-between gap-2 text-sm">
                    <span className="font-mono font-tabular text-ink/80">
                      {formatMoney(a.level, currencySymbol)}
                    </span>
                    <button
                      type="button"
                      onClick={() => onJumpToDate?.(a.date)}
                      className={`hover:underline ${a.date <= todayIso ? 'text-gold-deep' : 'text-accent'}`}
                      title="Show this day on the calendar"
                    >
                      {formatDateShort(fromISODate(a.date))} →
                    </button>
                  </li>
                ))}
              </ul>
              {hidden > 0 && (
                <p className="text-[11px] text-ink/70 mt-1">
                  and {hidden} more within three years
                </p>
              )}
            </div>
          )
        })()}

        {initial && achievements && achievements.length === 0 && (
          <p className="text-xs text-ink/70 mb-4">Not reached at any point in the next 3 years.</p>
        )}

        <Field label="Name">
          <input
            className={inputClass}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Emergency fund"
            required
            autoFocus
          />
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Balance to reach">
            <input
              className={inputClass}
              type="number"
              step="0.01"
              min="0"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              required
            />
          </Field>
          <Field label="Held for (days)" hint="1 = highlight the day you hit it">
            <input
              className={inputClass}
              type="number"
              step="1"
              min="1"
              value={consecutiveDays}
              onChange={(e) => setConsecutiveDays(e.target.value)}
              required
            />
          </Field>
        </div>

        <label className="flex items-start gap-2 mb-3 cursor-pointer">
          <input type="checkbox" className="mt-0.5" checked={ratchet} onChange={(e) => setRatchet(e.target.checked)} />
          <span className="text-xs font-sans text-ink/80">
            Raise the bar each time I sustain it
            <span className="block text-[11px] text-ink/70 mt-0.5">
              Once the run is complete the target steps up and starts again, so the calendar marks
              each rung as you clear it. Without this, it's marked once when you first reach it.
            </span>
          </span>
        </label>

        {ratchet && (
          <Field label="Raise by">
            <input
              className={inputClass}
              type="number"
              step="0.01"
              min="0"
              value={raiseBy}
              onChange={(e) => setRaiseBy(e.target.value)}
              placeholder="e.g. 250"
            />
          </Field>
        )}

        <Field label="Highlight style">
          <select className={inputClass} value={style} onChange={(e) => setStyle(e.target.value as BalanceTarget['style'])}>
            <option value="gold">Gold square</option>
            <option value="pulse">Throbbing ring (keeps the RAG colour)</option>
          </select>
        </Field>

        <label className="flex items-center gap-2 text-sm text-ink mb-5">
          <input type="checkbox" checked={active} onChange={(e) => setActive(e.target.checked)} />
          Active
        </label>

        <div className="flex items-center justify-between pt-3 border-t border-line mt-2">
          <div>
            {onDelete && (
              <button type="button" onClick={onDelete} className="text-sm text-rag-bad hover:underline">
                Delete
              </button>
            )}
          </div>
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="px-3 py-1.5 text-sm rounded-sm border border-line text-ink/80 hover:bg-paper">
              Cancel
            </button>
            {!initial && (
              <button
                type="submit"
                data-action="add-another"
                className="px-3 py-1.5 text-sm rounded-sm border border-accent text-accent hover:bg-accent/10"
              >
                Save &amp; add another
              </button>
            )}
            <button type="submit" data-action="save" className="px-3 py-1.5 text-sm rounded-sm bg-accent text-white hover:bg-accent/90">
              Save
            </button>
          </div>
        </div>
      </form>
    </Modal>
  )
}
